import * as styles from "./PdfViewer.css";

import { clsx } from "clsx";
import { type JSX, useId, useMemo, useState } from "react";

import { EmbedPDF } from "@embedpdf/core/react";
import { Spinner } from "@frontend/components/atoms/Spinner";
import { useLingui } from "@lingui/react/macro";

import { PdfViewerContext } from "./PdfViewer.context";
import type { PdfViewerProps } from "./PdfViewer.types";
import { PdfViewerDocument } from "./PdfViewerDocument";
import { PDF_VIEWER_TOOLS } from "./constants";
import { documentNameFrom } from "./documentName";
import { usePdfViewerEngine } from "./engine";
import { createPdfViewerPlugins } from "./plugins";

export function PdfViewer({
  src,
  name,
  password,
  defaultPage = 1,
  onPageChange,
  defaultZoom = "fit-width",
  onZoomChange,
  toolbar = true,
  tools = [...PDF_VIEWER_TOOLS],
  thumbnails = false,
  height = "40rem",
  onDocumentLoad,
  onError,
  onClose,
  className,
  style,
  ...props
}: PdfViewerProps): JSX.Element {
  const { t } = useLingui();

  const { engine, isLoading, error } = usePdfViewerEngine();

  const documentId = useId();

  const [thumbnailsOpen, setThumbnailsOpen] = useState(thumbnails);
  const [searchOpen, setSearchOpen] = useState(false);

  const documentName = name ?? documentNameFrom(src);

  const plugins = useMemo(
    () =>
      createPdfViewerPlugins({
        documentId,
        src,
        name: documentName,
        password,
        zoom: defaultZoom,
      }),
    [documentId, src, documentName, password, defaultZoom],
  );

  const context = useMemo(
    () => ({
      documentId,
      src,
      name: documentName,
      tools,
      thumbnailsOpen,
      setThumbnailsOpen,
      searchOpen,
      setSearchOpen,
      onClose,
    }),
    [documentId, src, documentName, tools, thumbnailsOpen, searchOpen, onClose],
  );

  return (
    <div
      data-slot="pdf-viewer"
      className={clsx(styles.root, className)}
      style={{ height, ...style }}
      {...props}
    >
      {error ? (
        <p className={clsx(styles.message, styles.messageError)}>
          {t`The PDF engine could not be loaded.`}
        </p>
      ) : engine === null || isLoading ? (
        <p className={styles.message}>
          <Spinner />
          {t`Loading the viewer`}
        </p>
      ) : (
        <PdfViewerContext.Provider value={context}>
          <EmbedPDF engine={engine} plugins={plugins}>
            <PdfViewerDocument
              defaultPage={defaultPage}
              onPageChange={onPageChange}
              onZoomChange={onZoomChange}
              onDocumentLoad={onDocumentLoad}
              onError={onError}
              toolbar={toolbar}
            />
          </EmbedPDF>
        </PdfViewerContext.Provider>
      )}
    </div>
  );
}
